/**
 * Search Query Validation Utility
 * Validates search queries for quality and appropriateness
 */

// Severe profanity that shows "[Filtered]" on dashboard
const SEVERE_PROFANITY = ['fuck', 'fucking', 'fucked','shit', 'shitty', 'bullshit','asshole', 'bitch','damn', 'dammit', 'goddamn','wtf', 'wth', 'fck', 'sht'];

// Moderate profanity that shows warning badge
const MODERATE_PROFANITY = ['stupid', 'dumb', 'useless', 'broken','garbage', 'trash', 'terrible', 'sucks', 'annoying'];

// Known keyboard mash patterns
const KEYBOARD_MASH_PATTERNS = ['asdf', 'qwerty', 'zxcv', 'jkl', 'fgh','asd','qwe','wasd','1234','abcd','test123','aaaa','bbbb'];

/**
 * Check if query is valid for searching
 * @param {string} query - The search query
 * @returns {object} - { isValid: boolean, message: string, severity: null|'warning'|'blocked' }
 */
export const validateSearchQuery = (query) => {
    if (!query || typeof query !== 'string') {
        return { isValid: false, message: 'Invalid query', severity: 'blocked' };
    }

    const trimmedQuery = query.trim();
    const lowerQuery = trimmedQuery.toLowerCase();

    // 1. At least 3 characters, the same rule as the server, so SSO, API and MFA run (K10 C7).
    // Removed 10 Oct 2026 (K10 C7): refusing 3 characters here ("<= 3") while the server accepts them.
    if (trimmedQuery.length < 3) {
        return { isValid: false, message: 'Query must be at least 3 characters', severity: 'blocked' };
    }

    // 2. Query with all same characters not allowed
    if (/^(.)\1+$/.test(trimmedQuery.replace(/\s/g, ''))) {
        return { isValid: false, message: 'Query contains only repeated characters', severity: 'blocked' };
    }

    // 3. Mostly non-alphabetic (>60% numbers, symbols, punctuation)
    const alphabeticCount = trimmedQuery.replace(/[^a-zA-Z]/g, '').length;
    const totalChars = trimmedQuery.replace(/\s/g, '').length;
    if (totalChars > 0 && alphabeticCount / totalChars < 0.4) {
        return { isValid: false, message: 'Query contains too many numbers or special characters', severity: 'blocked' };
    }

    // 4. Known keyboard mash patterns
    for (const pattern of KEYBOARD_MASH_PATTERNS) {
        if (lowerQuery.includes(pattern)) {
            return { isValid: false, message: 'Query appears to be keyboard mash', severity: 'blocked' };
        }
    }

    // 5. Repeated words (help help help)
    const words = trimmedQuery.toLowerCase().split(/\s+/);
    const wordCount = {};
    for (const word of words) {
        wordCount[word] = (wordCount[word] || 0) + 1;
    }
    const repeatedWord = Object.entries(wordCount).find(([_, count]) => count > 2);
    if (repeatedWord) {
        return { isValid: false, message: 'Query contains too many repeated words', severity: 'blocked' };
    }

    // 6. Single character repeated with spaces (a a a a)
    if (/^(\w\s+)+\w$/.test(trimmedQuery) && /^(.\s+)+.$/.test(trimmedQuery)) {
        const chars = trimmedQuery.split(/\s+/).filter(c => c.length === 1);
        if (chars.length > 3 && new Set(chars).size === 1) {
            return { isValid: false, message: 'Query contains single character repeated with spaces', severity: 'blocked' };
        }
    }

    // 7. Check for severe profanity
    for (const word of SEVERE_PROFANITY) {
        const regex = new RegExp(`\\b${word}\\b`, 'gi');
        if (regex.test(lowerQuery)) {
            return { 
                isValid: false, 
                message: 'Query contains inappropriate language', 
                severity: 'blocked',
                //displayText: '[Filtered]'
            };
        }
    }

    // 8. Check for moderate profanity
    for (const word of MODERATE_PROFANITY) {
        const regex = new RegExp(`\\b${word}\\b`, 'gi');
        if (regex.test(lowerQuery)) {
            return { 
                isValid: false, 
                message: 'Query contains inappropriate language', 
                severity: 'blocked',
                //displayText: '[Filtered]'
            };
        }
    }

    return { isValid: true, message: null, severity: null };
};

/**
 * Get validation status for UI display
 */
export const getValidationStatus = (validationResult) => {
    if (validationResult.severity === 'blocked') {
        return { canSearch: false, warning: validationResult.message };
    }
    if (validationResult.severity === 'blocked-content') {
        return { canSearch: false, warning: validationResult.message, displayText: '[Filtered]' };
    }
    if (validationResult.severity === 'warning') {
        return { canSearch: true, warning: validationResult.message };
    }
    return { canSearch: true, warning: null };
};
